import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { Search1Stroke } from '@lineiconshq/free-icons';
import type { ReactNode } from 'react';
import type {
  SearchFieldColumn,
  SearchFieldDialogAction,
  SearchFieldDialogActionContext,
  SearchRequestFn,
  SearchRequestResult
} from '../types';
import { DEFAULT_DIALOG_PAGE_SIZE, filterLocalRows, normalizeSearchColumns, resolveRowId } from '../helpers';
import type { DataTableColumn, DataTableRowSelectionModel } from '../../data-table-v2';
import { AritoIcon } from '@/components/custom/arito';
import { ErpDataTableV2 } from '../../data-table-v2';
import { ErpPagination } from '../../pagination';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface SearchResultsTableProps<T> {
  checkboxSelection?: boolean;
  dialogActions?: SearchFieldDialogAction<T>[];
  fillAvailableHeight?: boolean;
  headerFields?: ReactNode;
  hideRowNumberColumn?: boolean;
  onCloseDialog?: () => void;
  onRowDoubleClick?: (row: T) => void;
  onRowSelection?: (row: T | null) => void;
  onSelectionRowsChange?: (rows: T[]) => void;
  open: boolean;
  pageSize?: number;
  refreshKey?: string | number;
  rows?: T[];
  searchColumns?: SearchFieldColumn<T>[];
  searchRequest: SearchRequestFn<T> | null;
  selectedRows?: T[];
  selectedRowId?: string | null;
  stretchTableWidth?: boolean;
}

const MIN_DIALOG_DATA_COLUMNS_WIDTH = 490;

const getColumnWidth = (column: DataTableColumn) => {
  if (typeof column.width === 'number') return column.width;
  if (typeof column.minWidth === 'number') return column.minWidth;
  return 120;
};

const scaleColumnsToDialogWidth = (columns: DataTableColumn[]) => {
  const currentWidth = columns.reduce((total, column) => total + getColumnWidth(column), 0);

  if (currentWidth >= MIN_DIALOG_DATA_COLUMNS_WIDTH || columns.length === 0) {
    return columns;
  }

  const extraWidth = MIN_DIALOG_DATA_COLUMNS_WIDTH - currentWidth;

  return columns.map(column => {
    const width = getColumnWidth(column);
    const ratio = width / currentWidth;
    return {
      ...column,
      width: Math.round(width + extraWidth * ratio)
    };
  });
};

export const SearchResultsTable = <T,>({
  checkboxSelection = false,
  dialogActions = [],
  fillAvailableHeight = false,
  headerFields,
  hideRowNumberColumn = false,
  onCloseDialog,
  onRowDoubleClick,
  onRowSelection,
  onSelectionRowsChange,
  open,
  pageSize = DEFAULT_DIALOG_PAGE_SIZE,
  refreshKey,
  rows,
  searchColumns = [],
  searchRequest,
  selectedRows = [],
  selectedRowId,
  stretchTableWidth = false
}: SearchResultsTableProps<T>) => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [remoteRows, setRemoteRows] = useState<T[]>([]);
  const [remoteTotalItems, setRemoteTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);
  const remotePageCacheRef = useRef(new Map<string, SearchRequestResult<T>>());
  const prefetchingPageKeysRef = useRef(new Set<string>());
  const shouldUseServer = Boolean(searchRequest);
  const tableColumns = useMemo(() => scaleColumnsToDialogWidth(normalizeSearchColumns(searchColumns)), [searchColumns]);

  const buildRequestCacheKey = useCallback(
    (page: number, query: string) => `${page}|${pageSize}|${query.trim()}`,
    [pageSize]
  );

  const fetchRemotePage = useCallback(
    async (page: number, query: string) => {
      const normalizedQuery = query.trim();
      const cacheKey = buildRequestCacheKey(page, normalizedQuery);
      const cachedResult = remotePageCacheRef.current.get(cacheKey);

      if (cachedResult) {
        return cachedResult;
      }

      const result = await searchRequest?.({
        page,
        pageSize,
        query: normalizedQuery
      });

      const resolvedResult = result ?? { items: [], total: 0 };
      remotePageCacheRef.current.set(cacheKey, resolvedResult);
      return resolvedResult;
    },
    [buildRequestCacheKey, pageSize, searchRequest]
  );

  const prefetchRemotePage = useCallback(
    async (page: number, query: string) => {
      if (!searchRequest) {
        return;
      }

      const normalizedQuery = query.trim();
      const cacheKey = buildRequestCacheKey(page, normalizedQuery);

      if (remotePageCacheRef.current.has(cacheKey) || prefetchingPageKeysRef.current.has(cacheKey)) {
        return;
      }

      prefetchingPageKeysRef.current.add(cacheKey);

      try {
        const result = await searchRequest({
          page,
          pageSize,
          query: normalizedQuery
        });

        remotePageCacheRef.current.set(cacheKey, result);
      } finally {
        prefetchingPageKeysRef.current.delete(cacheKey);
      }
    },
    [buildRequestCacheKey, pageSize, searchRequest]
  );

  useEffect(() => {
    remotePageCacheRef.current.clear();
    prefetchingPageKeysRef.current.clear();
    setRemoteRows([]);
    setRemoteTotalItems(0);
  }, [pageSize, refreshKey, searchRequest]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchValue(searchValue.trim());
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [searchValue]);

  const previousSearchValueRef = useRef<string | null>(null);

  useEffect(() => {
    if (previousSearchValueRef.current === null) {
      previousSearchValueRef.current = debouncedSearchValue;
      return;
    }

    if (previousSearchValueRef.current !== debouncedSearchValue) {
      previousSearchValueRef.current = debouncedSearchValue;
      onRowSelection?.(null);
      onSelectionRowsChange?.([]);
    }
  }, [debouncedSearchValue, onRowSelection, onSelectionRowsChange]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearchValue, open]);

  useEffect(() => {
    if (!open || !shouldUseServer) {
      setIsLoading(false);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const cacheKey = buildRequestCacheKey(currentPage, debouncedSearchValue);
    const cachedResult = remotePageCacheRef.current.get(cacheKey);

    if (cachedResult) {
      setRemoteRows(cachedResult.items);
      setRemoteTotalItems(cachedResult.total);
      setIsLoading(false);
    } else {
      setRemoteRows([]);
      setRemoteTotalItems(0);
      setIsLoading(true);
    }

    fetchRemotePage(currentPage, debouncedSearchValue)
      .then(result => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        setRemoteRows(result.items);
        setRemoteTotalItems(result.total);

        const totalPages = Math.ceil(result.total / pageSize);
        const nextPage = currentPage + 1;

        if (nextPage < totalPages) {
          void prefetchRemotePage(nextPage, debouncedSearchValue);
        }
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        setRemoteRows([]);
        setRemoteTotalItems(0);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      });
  }, [
    buildRequestCacheKey,
    currentPage,
    debouncedSearchValue,
    fetchRemotePage,
    open,
    pageSize,
    prefetchRemotePage,
    refreshKey,
    shouldUseServer
  ]);

  const filteredLocalRows = useMemo(
    () => filterLocalRows(rows ?? [], debouncedSearchValue),
    [debouncedSearchValue, rows]
  );

  const paginatedLocalRows = useMemo(() => {
    const startIndex = currentPage * pageSize;
    return filteredLocalRows.slice(startIndex, startIndex + pageSize);
  }, [currentPage, filteredLocalRows, pageSize]);

  const tableRows = shouldUseServer ? remoteRows : paginatedLocalRows;
  const totalItems = shouldUseServer ? remoteTotalItems : filteredLocalRows.length;
  const selectedRowIds = useMemo(() => selectedRows.map(row => resolveRowId(row)), [selectedRows]);

  useEffect(() => {
    if (!selectedRowId) {
      return;
    }

    const hasSelectedRow = tableRows.some(row => resolveRowId(row) === selectedRowId);

    if (!hasSelectedRow) {
      onRowSelection?.(null);
    }
  }, [onRowSelection, selectedRowId, tableRows]);

  const handleSelectionModelChange = useCallback(
    (nextSelectionModel: DataTableRowSelectionModel) => {
      if (!checkboxSelection) {
        return;
      }

      const nextSelectedIdSet = new Set(nextSelectionModel.map(value => String(value)));
      const currentPageIds = new Set(tableRows.map(row => resolveRowId(row)));
      const retainedRows = selectedRows.filter(row => !currentPageIds.has(resolveRowId(row)));
      const nextSelectedRows = tableRows.filter(row => nextSelectedIdSet.has(resolveRowId(row)));

      onSelectionRowsChange?.([...retainedRows, ...nextSelectedRows]);
    },
    [checkboxSelection, onSelectionRowsChange, selectedRows, tableRows]
  );

  const actionContext = useMemo<SearchFieldDialogActionContext<T>>(
    () => ({
      closeDialog: () => onCloseDialog?.(),
      selectedRow: tableRows.find(row => resolveRowId(row) === selectedRowId) ?? null,
      selectedRows
    }),
    [onCloseDialog, selectedRowId, selectedRows, tableRows]
  );

  const visibleDialogActions = useMemo(
    () =>
      dialogActions.filter(action =>
        typeof action.hidden === 'function' ? !action.hidden(actionContext) : !action.hidden
      ),
    [actionContext, dialogActions]
  );

  return (
    <div className={cn('flex min-h-0 min-w-0 flex-col overflow-hidden bg-white', fillAvailableHeight && 'flex-1')}>
      <div className='relative z-20 flex min-w-0 items-center gap-3 border-b border-[#d9e0ea] bg-white px-4 py-2'>
        <div className='relative w-full max-w-[420px] flex-none'>
          <Lineicons
            aria-hidden='true'
            icon={Search1Stroke}
            className='pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-main'
          />
          <Input
            value={searchValue}
            onChange={event => setSearchValue(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                setDebouncedSearchValue(event.currentTarget.value.trim());
                setCurrentPage(0);
              }
            }}
            placeholder='Tìm kiếm...'
            className='rounded-none border-[#cfd8e3] bg-white pl-10 shadow-none focus-visible:ring-0'
            style={{ height: 'var(--erp-control-height)', fontSize: 14 }}
          />
        </div>

        {visibleDialogActions.length > 0 && (
          <div className='flex flex-none items-center gap-1'>
            {visibleDialogActions.map(action => {
              const disabled = typeof action.disabled === 'function' ? action.disabled(actionContext) : action.disabled;

              return (
                <button
                  key={action.key}
                  type='button'
                  onClick={() => action.onClick(actionContext)}
                  disabled={disabled}
                  className='inline-flex size-8 items-center justify-center border border-[#cfd8e3] bg-white text-slate-700 transition-colors hover:bg-slate-50 hover:text-main disabled:pointer-events-none disabled:opacity-50'
                  title={action.title}
                >
                  <AritoIcon icon={action.icon} />
                </button>
              );
            })}
          </div>
        )}

        {headerFields}
      </div>

      {totalItems > 0 && (
        <div className='relative z-20 border-b border-[#d9e0ea] bg-white px-2'>
          <ErpPagination
            paginationModel={{ page: currentPage, pageSize }}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      <div className={cn('min-h-0 min-w-0 overflow-hidden border-b border-[#d9e0ea]', fillAvailableHeight && 'flex-1')}>
        <ErpDataTableV2
          tables={[
            {
              name: '',
              rows: tableRows,
              columns: tableColumns,
              checkboxSelection,
              selectionModel: selectedRowIds,
              onSelectionModelChange: handleSelectionModelChange,
              disableColumnFilters: true
            }
          ]}
          hideHeader
          showIndexColumn={!hideRowNumberColumn}
          loading={isLoading}
          selectedRowId={selectedRowId || undefined}
          widthMode={stretchTableWidth ? 'stretch' : 'content'}
          scrollMode='auto'
          getRowId={resolveRowId}
          onRowClick={params => onRowSelection?.(params.row as T)}
          onRowDoubleClick={params => onRowDoubleClick?.(params.row as T)}
          className={fillAvailableHeight ? 'h-full min-h-0 w-full' : '!h-auto w-full'}
        />
      </div>
    </div>
  );
};
